'use client';
import React, { useState, useRef, useEffect } from 'react';

type Status = 'idle' | 'loading-model' | 'processing' | 'done' | 'error';

interface ProgressEvent {
  key: string;
  loaded: number;
  total: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface BackgroundRemoverToolProps {
  onStatusChange?: (status: Status) => void;
}

export default function BackgroundRemoverTool({ onStatusChange }: BackgroundRemoverToolProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isFirstRun, setIsFirstRun] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if model was previously loaded in this session
    const loaded = sessionStorage.getItem('bgremover_model_loaded');
    if (loaded) setIsFirstRun(false);
  }, []);

  const updateStatus = (s: Status) => {
    setStatus(s);
    onStatusChange?.(s);
  };

  const processImage = async (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp|gif|bmp|tiff)$/)) {
      setErrorMsg('Please upload a JPG, PNG, WebP, GIF, BMP, or TIFF image.');
      updateStatus('error');
      return;
    }
    setFileName(file.name);
    setOriginalSize(file.size);
    setOriginalUrl(URL.createObjectURL(file));
    setResultUrl(null);
    setErrorMsg('');
    setProgress(0);
    setProgressLabel('Initializing…');
    updateStatus('loading-model');

    try {
      const { removeBackground } = await import('@imgly/background-removal');

      setProgressLabel('Loading AI model…');
      setProgress(10);

      const blob = await removeBackground(file, {
        progress: (key: string, current: number, total: number) => {
          if (total > 0) {
            const pct = Math.round((current / total) * 100);
            if (key.includes('fetch') || key.includes('load')) {
              setProgressLabel(`Loading model… ${pct}%`);
              setProgress(Math.min(10 + Math.round(pct * 0.6), 70));
              updateStatus('loading-model');
            } else {
              setProgressLabel(`Removing background… ${pct}%`);
              setProgress(Math.min(70 + Math.round(pct * 0.28), 98));
              updateStatus('processing');
            }
          }
        },
      } as Parameters<typeof removeBackground>[1]);

      sessionStorage.setItem('bgremover_model_loaded', '1');
      setIsFirstRun(false);
      setProgress(100);
      setProgressLabel('Done!');
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      updateStatus('done');
    } catch (err) {
      console.error(err);
      setErrorMsg('Background removal failed. Please try a different image or refresh the page.');
      updateStatus('error');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processImage(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processImage(f);
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `bg-removed_${fileName.replace(/\.[^.]+$/, '')}.png`;
    a.click();
  };

  const handleReset = () => {
    updateStatus('idle');
    setOriginalUrl(null);
    setResultUrl(null);
    setErrorMsg('');
    setProgress(0);
    setProgressLabel('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isProcessing = status === 'loading-model' || status === 'processing';

  return (
    <div className="space-y-5">
      {/* First-run warning */}
      {isFirstRun && status === 'idle' && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div>
            <p className="text-amber-800 text-sm font-semibold">First run may take longer</p>
            <p className="text-amber-700 text-xs mt-0.5">The AI model (~40 MB) loads in your browser the first time. Subsequent uses are instant.</p>
          </div>
        </div>
      )}

      {/* Upload zone */}
      {status === 'idle' && (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
            isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800 mb-1">Upload Image</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports JPG, PNG, WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Processing state */}
      {isProcessing && (
        <div className="space-y-4">
          {/* Original preview while processing */}
          {originalUrl && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original</p>
              <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50 h-48 sm:h-64 flex items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={originalUrl} alt="Original" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
          )}

          {/* Progress card */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full border-3 border-blue-500 border-t-transparent animate-spin shrink-0" style={{ borderWidth: 3 }} />
              <div>
                <p className="font-semibold text-gray-800 text-sm">
                  {status === 'loading-model' ? 'Loading AI model…' : 'Removing background…'}
                </p>
                <p className="text-gray-500 text-xs mt-0.5">{progressLabel}</p>
              </div>
              <span className="ml-auto text-blue-600 font-bold text-sm">{progress}%</span>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            {status === 'loading-model' && (
              <p className="text-blue-600 text-xs mt-3 text-center">
                ⏳ First load downloads the AI model (~40 MB). This only happens once.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div>
              <p className="text-red-700 text-sm font-semibold">Processing failed</p>
              <p className="text-red-600 text-xs mt-0.5">{errorMsg || 'Please try again with a different image.'}</p>
            </div>
          </div>
          <button onClick={handleReset} className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">
            Try Again
          </button>
        </div>
      )}

      {/* Done state */}
      {status === 'done' && originalUrl && resultUrl && (
        <div className="space-y-5">
          {/* Side-by-side preview */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-center">Original</p>
              <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50 aspect-square flex items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={originalUrl} alt="Original" className="w-full h-full object-contain" />
              </div>
              <p className="text-xs text-gray-400 text-center mt-1.5">{formatBytes(originalSize)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-center">Result (PNG)</p>
              <div
                className="rounded-xl overflow-hidden border border-gray-100 aspect-square flex items-center justify-center"
                style={{ backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, white 0% 50%)', backgroundSize: '20px 20px' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={resultUrl} alt="Background removed" className="w-full h-full object-contain" />
              </div>
              <p className="text-xs text-green-600 font-medium text-center mt-1.5">Transparent background ✓</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex-1 py-3.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 60%, #1e40af 100%)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download PNG
            </button>
            <button
              onClick={handleReset}
              className="px-5 py-3.5 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              New Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
